import './App.css';
import { useEffect, useRef, useState, useCallback, useMemo, memo } from 'react';
import { motion, useScroll, useTransform, useSpring, useInView, useMotionValue } from 'framer-motion';
import * as THREE from 'three';

/* ═══════════════════════════════════════════════════
   Configurable site origin — default to the HTTPS
   domain. Never hardcode a raw IP / http:// link.
   ═══════════════════════════════════════════════════ */
const SITE_URL = 'https://med.thinksigma.ai';
const GITHUB_URL = 'https://github.com/0xV8/nightglass';
const RELEASES_URL = `${GITHUB_URL}/releases`;
const PRIVACY_URL = `${SITE_URL}/privacy-policy`;
// TODO(backend): replace with a real waitlist endpoint. Front-end only for now.
const WAITLIST_ENDPOINT = ''; // e.g. 'https://api.thinksigma.ai/waitlist'
const WAITLIST_MAILTO = 'contact@thinksigma.ai';
// TODO(stores): set real store listing URLs once published.
const PLAY_STORE_URL = ''; // 'https://play.google.com/store/apps/details?id=...'
const APP_STORE_URL = ''; // 'https://apps.apple.com/app/...'

/* ═══════════════════════════════════════════════════
   3D Scene — Lerp-smoothed, mouse-tracked,
   visibility-paused, fully disposed
   ═══════════════════════════════════════════════════ */
function ThreeScene({ scrollProgress }) {
  const mountRef = useRef(null);
  const sceneRef = useRef({});
  const mouseRef = useRef({ x: 0, y: 0 });
  const targetRef = useRef({ scrollProgress: 0 });

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(60, mount.clientWidth / mount.clientHeight, 0.1, 1000);
    camera.position.z = 5;

    const isMobileDevice = window.innerWidth < 768;
    const renderer = new THREE.WebGLRenderer({
      alpha: true,
      antialias: !isMobileDevice,
      powerPreference: isMobileDevice ? 'low-power' : 'high-performance',
    });
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, isMobileDevice ? 1.5 : 2));
    renderer.setClearColor(0x000000, 0);
    mount.appendChild(renderer.domElement);

    // Torus knot — reduced segments for wireframe
    const torusGeo = new THREE.TorusKnotGeometry(1.2, 0.35, isMobileDevice ? 64 : 100, 16);
    const torusMat = new THREE.MeshBasicMaterial({
      color: 0x4a8c6f, wireframe: true, transparent: true, opacity: 0.25,
    });
    const torusKnot = new THREE.Mesh(torusGeo, torusMat);
    scene.add(torusKnot);

    // Icosahedron
    const icoGeo = new THREE.IcosahedronGeometry(0.7, isMobileDevice ? 0 : 1);
    const icoMat = new THREE.MeshBasicMaterial({
      color: 0x7eb8c9, wireframe: true, transparent: true, opacity: 0.15,
    });
    const ico = new THREE.Mesh(icoGeo, icoMat);
    scene.add(ico);

    // Octahedron
    const octGeo = new THREE.OctahedronGeometry(2.0, 0);
    const octMat = new THREE.MeshBasicMaterial({
      color: 0x7b68ee, wireframe: true, transparent: true, opacity: 0.1,
    });
    const oct = new THREE.Mesh(octGeo, octMat);
    scene.add(oct);

    // Particles — tiered count
    const particleCount = isMobileDevice ? 80 : 200;
    const positions = new Float32Array(particleCount * 3);
    const velocities = new Float32Array(particleCount * 3);
    for (let i = 0; i < particleCount * 3; i++) {
      positions[i] = (Math.random() - 0.5) * 12;
      velocities[i] = (Math.random() - 0.5) * 0.002;
    }
    const particleGeo = new THREE.BufferGeometry();
    particleGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    const particleMat = new THREE.PointsMaterial({
      color: 0x4a8c6f, size: 0.018, transparent: true, opacity: 0.35, sizeAttenuation: true,
    });
    const particles = new THREE.Points(particleGeo, particleMat);
    scene.add(particles);

    sceneRef.current = { scene, camera, renderer, torusKnot, ico, oct, particles, particleGeo, velocities, particleCount };

    const lerp = (a, b, t) => a + (b - a) * t;
    const smooth = {
      scrollProg: 0, mouseX: 0, mouseY: 0,
      torusRotX: 0, torusRotY: 0, icoRotX: 0, icoRotZ: 0,
      octRotY: 0, octRotZ: 0, camZ: 5, camY: 0,
    };

    let animationId;
    let startTime = performance.now();

    const animate = () => {
      animationId = requestAnimationFrame(animate);

      // Skip render when tab is hidden
      if (document.hidden) return;

      const time = (performance.now() - startTime) * 0.0005;
      smooth.scrollProg = lerp(smooth.scrollProg, targetRef.current.scrollProgress, 0.04);
      const sp = smooth.scrollProg;

      smooth.mouseX = lerp(smooth.mouseX, mouseRef.current.x, 0.05);
      smooth.mouseY = lerp(smooth.mouseY, mouseRef.current.y, 0.05);

      const tRX = time * 0.4 + sp * Math.PI * 2 + smooth.mouseY * 0.3;
      const tRY = time * 0.6 + sp * Math.PI * 4 + smooth.mouseX * 0.3;
      smooth.torusRotX = lerp(smooth.torusRotX, tRX, 0.06);
      smooth.torusRotY = lerp(smooth.torusRotY, tRY, 0.06);
      smooth.icoRotX = lerp(smooth.icoRotX, -time * 0.5 + sp * Math.PI * -1.5, 0.06);
      smooth.icoRotZ = lerp(smooth.icoRotZ, time * 0.4 + sp * Math.PI * -3 + smooth.mouseX * 0.2, 0.06);
      smooth.octRotY = lerp(smooth.octRotY, time * 0.2 + sp * Math.PI * 2, 0.06);
      smooth.octRotZ = lerp(smooth.octRotZ, -time * 0.3 + smooth.mouseY * 0.15, 0.06);

      torusKnot.rotation.set(smooth.torusRotX, smooth.torusRotY, 0);
      ico.rotation.set(smooth.icoRotX, 0, smooth.icoRotZ);
      oct.rotation.set(0, smooth.octRotY, smooth.octRotZ);

      const breathe = 1 + Math.sin(time * 2) * 0.05;
      torusKnot.scale.setScalar(breathe);
      ico.scale.setScalar(1.1 - Math.sin(time * 2) * 0.05);

      smooth.camZ = lerp(smooth.camZ, 5 + sp * 3, 0.04);
      smooth.camY = lerp(smooth.camY, sp * -1.5, 0.04);
      camera.position.set(smooth.mouseX * 0.5, smooth.camY, smooth.camZ);

      torusMat.opacity = Math.max(0.05, 0.25 - sp * 0.15);
      icoMat.opacity = Math.max(0.03, 0.15 - sp * 0.1);
      octMat.opacity = Math.max(0.02, 0.1 - sp * 0.05);

      // Animate particles
      const posArray = particleGeo.attributes.position.array;
      for (let i = 0; i < particleCount * 3; i++) {
        posArray[i] += velocities[i];
        if (Math.abs(posArray[i]) > 6) velocities[i] *= -1;
      }
      particleGeo.attributes.position.needsUpdate = true;
      particles.rotation.y = time * 0.06;

      renderer.render(scene, camera);
    };
    animate();

    const handleMouse = (e) => {
      mouseRef.current.x = (e.clientX / window.innerWidth - 0.5) * 2;
      mouseRef.current.y = (e.clientY / window.innerHeight - 0.5) * 2;
    };

    let resizeTimer = null;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        if (!mount) return;
        camera.aspect = mount.clientWidth / mount.clientHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(mount.clientWidth, mount.clientHeight);
      }, 150);
    };

    // WebGL context loss recovery
    const handleContextLost = (e) => { e.preventDefault(); cancelAnimationFrame(animationId); };
    const handleContextRestored = () => { animate(); };
    renderer.domElement.addEventListener('webglcontextlost', handleContextLost);
    renderer.domElement.addEventListener('webglcontextrestored', handleContextRestored);

    window.addEventListener('mousemove', handleMouse, { passive: true });
    window.addEventListener('resize', handleResize, { passive: true });

    return () => {
      cancelAnimationFrame(animationId);
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mousemove', handleMouse);
      renderer.domElement.removeEventListener('webglcontextlost', handleContextLost);
      renderer.domElement.removeEventListener('webglcontextrestored', handleContextRestored);

      // Full Three.js disposal
      scene.traverse((obj) => {
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) {
          if (Array.isArray(obj.material)) obj.material.forEach(m => m.dispose());
          else obj.material.dispose();
        }
      });
      renderer.dispose();
      renderer.forceContextLoss();

      if (mount && renderer.domElement.parentNode === mount) {
        mount.removeChild(renderer.domElement);
      }
    };
  }, []);

  useEffect(() => {
    targetRef.current.scrollProgress = scrollProgress || 0;
  }, [scrollProgress]);

  return <div ref={mountRef} className="three-canvas" aria-hidden="true" />;
}

/* ═══════════════════════════════════════════════════
   Animated counter — rAF properly cancelled
   ═══════════════════════════════════════════════════ */
const AnimatedCounter = memo(function AnimatedCounter({ value, delay = 0 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const num = parseInt(value, 10);

  useEffect(() => {
    if (!isInView) return;
    let rafId;
    const timeout = setTimeout(() => {
      const duration = 1200;
      const startTime = Date.now();
      const tick = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        setCount(Math.round(eased * num));
        if (progress < 1) rafId = requestAnimationFrame(tick);
      };
      rafId = requestAnimationFrame(tick);
    }, delay * 1000);
    return () => { clearTimeout(timeout); if (rafId) cancelAnimationFrame(rafId); };
  }, [isInView, num, delay]);

  return <span ref={ref}>{count}</span>;
});

/* ═══════════════════════════════════════════════════
   Text split reveal — with string guard
   ═══════════════════════════════════════════════════ */
const SplitText = memo(function SplitText({ children, className, delay = 0 }) {
  const text = String(children || '');
  const words = text.split(' ');
  return (
    <span className={className} aria-label={text}>
      {words.map((word, i) => (
        <span key={word + i} className="word-wrapper" aria-hidden="true">
          <motion.span
            className="word"
            initial={{ y: '110%', opacity: 0 }}
            animate={{ y: '0%', opacity: 1 }}
            transition={{ duration: 0.5, delay: delay + i * 0.08, ease: [0.25, 0.4, 0, 1] }}
          >
            {word}
          </motion.span>
          {i < words.length - 1 && ' '}
        </span>
      ))}
    </span>
  );
});

/* ═══════════════════════════════════════════════════
   Scroll-reveal — no blur (GPU perf fix)
   ═══════════════════════════════════════════════════ */
const revealVariants = {
  up: { hidden: { opacity: 0, y: 40 }, visible: { opacity: 1, y: 0 } },
  down: { hidden: { opacity: 0, y: -40 }, visible: { opacity: 1, y: 0 } },
  left: { hidden: { opacity: 0, x: 40 }, visible: { opacity: 1, x: 0 } },
  right: { hidden: { opacity: 0, x: -40 }, visible: { opacity: 1, x: 0 } },
};

const Reveal = memo(function Reveal({ children, delay = 0, direction = 'up' }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  const v = revealVariants[direction] || revealVariants.up;

  return (
    <motion.div
      ref={ref}
      initial={v.hidden}
      animate={isInView ? v.visible : v.hidden}
      transition={{ duration: 0.6, delay, ease: [0.25, 0.4, 0, 1] }}
    >
      {children}
    </motion.div>
  );
});

/* ═══════════════════════════════════════════════════
   Magnetic hover card
   ═══════════════════════════════════════════════════ */
const MagneticCard = memo(function MagneticCard({ children, className, style, strength = 12, as = 'div' }) {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const springX = useSpring(x, { stiffness: 200, damping: 20 });
  const springY = useSpring(y, { stiffness: 200, damping: 20 });

  const handleMouse = useCallback((e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    x.set((e.clientX - rect.left - rect.width / 2) / rect.width * strength);
    y.set((e.clientY - rect.top - rect.height / 2) / rect.height * strength);
  }, [x, y, strength]);

  const handleLeave = useCallback(() => { x.set(0); y.set(0); }, [x, y]);

  const MotionTag = motion[as] || motion.div;

  return (
    <MotionTag
      ref={ref}
      className={className}
      style={{ ...style, x: springX, y: springY }}
      onMouseMove={handleMouse}
      onMouseLeave={handleLeave}
    >
      {children}
    </MotionTag>
  );
});

/* ═══════════════════════════════════════════════════
   Feature Card
   ═══════════════════════════════════════════════════ */
const FeatureCard = memo(function FeatureCard({ icon, title, description, accent, index }) {
  return (
    <Reveal delay={index * 0.15}>
      <MagneticCard className="feature-card" style={{ '--accent': accent }} strength={8}>
        <div className="feature-card-glow" />
        <div className="feature-card-content">
          <div className="feature-icon" aria-hidden="true">{icon}</div>
          <h3 className="feature-title">{title}</h3>
          <p className="feature-desc">{description}</p>
        </div>
      </MagneticCard>
    </Reveal>
  );
});

const ScienceItem = memo(function ScienceItem({ text, source, index }) {
  return (
    <Reveal delay={index * 0.08}>
      <div className="science-item">
        <div className="science-dot" aria-hidden="true" />
        <div>
          <p className="science-text">{text}</p>
          <p className="science-source">{source}</p>
        </div>
      </div>
    </Reveal>
  );
});

/* ═══════════════════════════════════════════════════
   Waitlist / email capture — front-end only.
   Posts to a placeholder endpoint when configured,
   otherwise falls back to a mailto link. Clearly a TODO.
   ═══════════════════════════════════════════════════ */
const Waitlist = memo(function Waitlist() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | submitting | done | error

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    const value = email.trim();
    if (!value || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setStatus('error');
      return;
    }

    // No real backend yet. If an endpoint is configured, POST to it;
    // otherwise open the user's mail client as a graceful fallback.
    if (WAITLIST_ENDPOINT) {
      try {
        setStatus('submitting');
        await fetch(WAITLIST_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: value }),
        });
        setStatus('done');
        setEmail('');
      } catch {
        setStatus('error');
      }
    } else {
      window.location.href = `mailto:${WAITLIST_MAILTO}?subject=${encodeURIComponent('Nightglass Plus waitlist')}&body=${encodeURIComponent(`Please add me to the waitlist: ${value}`)}`;
      setStatus('done');
    }
  }, [email]);

  return (
    <form className="waitlist" onSubmit={handleSubmit} noValidate>
      <label htmlFor="waitlist-email" className="waitlist-label">
        Join the Nightglass Plus waitlist
      </label>
      <div className="waitlist-row">
        <input
          id="waitlist-email"
          type="email"
          inputMode="email"
          autoComplete="email"
          className="waitlist-input"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => { setEmail(e.target.value); if (status === 'error') setStatus('idle'); }}
          aria-invalid={status === 'error'}
          aria-describedby="waitlist-help"
        />
        <motion.button
          type="submit"
          className="btn-primary"
          whileHover={{ scale: 1.04 }}
          whileTap={{ scale: 0.96 }}
          disabled={status === 'submitting'}
        >
          {status === 'submitting' ? 'Joining…' : 'Notify me'}
        </motion.button>
      </div>
      <p id="waitlist-help" className="waitlist-help" role="status">
        {status === 'done' && 'Thanks — we will be in touch. (No charge today.)'}
        {status === 'error' && 'Please enter a valid email address.'}
        {(status === 'idle' || status === 'submitting') &&
          'Optional. We only email you about Nightglass Plus. No spam, unsubscribe anytime.'}
      </p>
    </form>
  );
});

/* ═══════════════════════════════════════════════════
   Main App
   ═══════════════════════════════════════════════════ */
function App() {
  const [scrollY, setScrollY] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const containerRef = useRef(null);
  const isMobile = useRef(typeof window !== 'undefined' && window.innerWidth < 640);

  const { scrollYProgress } = useScroll();
  const smoothProgress = useSpring(scrollYProgress, { stiffness: 100, damping: 30, restDelta: 0.001 });

  const heroOpacity = useTransform(scrollYProgress, [0, 0.18], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 0.22], [1, 0.92]);
  const heroY = useTransform(scrollYProgress, [0, 0.2], [0, -80]);

  // RAF-throttled scroll handler
  const handleScroll = useCallback(() => {
    const y = window.scrollY;
    setScrollY(y);
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    setScrollProgress(docHeight > 0 ? y / docHeight : 0);
  }, []);

  useEffect(() => {
    let rafId = null;
    const onScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        handleScroll();
        rafId = null;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [handleScroll]);

  const scrollToId = useCallback((id) => (e) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const stats = useMemo(() => [
    { value: '16', label: 'Guided sessions' },
    { value: '12', label: 'Ambient sounds' },
    { value: '5', label: 'Breathing patterns' },
    { value: '7', label: 'Video backgrounds' },
  ], []);

  return (
    <div className="app" ref={containerRef}>
      {/* 3D Background — skip on mobile for perf */}
      {!isMobile.current && (
        <div className="three-wrapper" aria-hidden="true">
          <ThreeScene scrollProgress={scrollProgress} />
        </div>
      )}

      {/* Ambient glow orbs */}
      <div className="ambient-glow glow-1" aria-hidden="true" />
      <div className="ambient-glow glow-2" aria-hidden="true" />
      <div className="ambient-glow glow-3" aria-hidden="true" />

      {/* Scroll progress bar */}
      <motion.div className="scroll-progress" style={{ scaleX: smoothProgress }} aria-hidden="true" />

      {/* Skip link for keyboard / screen-reader users */}
      <a href="#main" className="skip-link">Skip to content</a>

      {/* Navigation */}
      <motion.nav
        className={`nav ${scrollY > 50 ? 'nav-scrolled' : ''}`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, ease: [0.25, 0.4, 0, 1] }}
        aria-label="Main navigation"
      >
        <div className="nav-container">
          <div className="nav-brand">
            <span className="nav-logo" aria-hidden="true">◉</span>
            <span>Nightglass</span>
          </div>
          <nav className="nav-links" aria-label="Section navigation">
            <a href="#features" onClick={scrollToId('features')}>Features</a>
            <a href="#ai" onClick={scrollToId('ai')}>AI &amp; safety</a>
            <a href="#pricing" onClick={scrollToId('pricing')}>Pricing</a>
            <a href="#get" className="nav-download" onClick={scrollToId('get')}>Get the app</a>
          </nav>
        </div>
      </motion.nav>

      <main id="main">
        {/* ─── Hero Section ─── */}
        <section className="hero" id="hero">
          <motion.div className="hero-content" style={{ opacity: heroOpacity, scale: heroScale, y: heroY }}>
            <motion.div
              className="hero-badge"
              initial={{ opacity: 0, y: 20, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: 0.3, duration: 0.6 }}
            >
              Private by default &middot; On-device AI &middot; Research-based
            </motion.div>

            <h1 className="hero-title">
              <SplitText delay={0.5}>Find your</SplitText>
              <br />
              <span className="hero-title-accent">
                <SplitText className="hero-title-accent-inner" delay={0.8}>inner peace</SplitText>
              </span>
            </h1>

            <motion.p
              className="hero-subtitle"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 0.8 }}
            >
              A meditation app grounded in neuroscience. Private by default —
              the core is free, your data stays on your phone, and the AI
              companion runs on your phone too.
            </motion.p>

            <motion.div
              className="hero-actions"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.3, duration: 0.6 }}
            >
              <motion.a
                href="#get"
                className="btn-primary"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={scrollToId('get')}
                aria-label="Get Nightglass — see download options"
              >
                <span className="btn-icon" aria-hidden="true">↓</span>
                Get the app
              </motion.a>
              <motion.a
                href="#features"
                className="btn-secondary"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.97 }}
                onClick={scrollToId('features')}
              >
                Explore features
              </motion.a>
            </motion.div>

            <motion.div
              className="hero-stats"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.6, duration: 0.8 }}
            >
              {stats.map((stat, i) => (
                <div key={stat.label} className="hero-stat-group">
                  {i > 0 && <div className="hero-stat-divider" aria-hidden="true" />}
                  <div className="hero-stat">
                    <span className="hero-stat-value">
                      <AnimatedCounter value={stat.value} delay={1.8 + i * 0.15} />
                    </span>
                    <span className="hero-stat-label">{stat.label}</span>
                  </div>
                </div>
              ))}
            </motion.div>
          </motion.div>

          {/* Scroll indicator — CSS driven visibility */}
          <div className={`scroll-indicator ${scrollY > 100 ? 'scroll-indicator--hidden' : ''}`} aria-hidden="true">
            <div className="scroll-mouse">
              <div className="scroll-mouse-dot" />
            </div>
            <span className="scroll-text">Scroll to explore</span>
          </div>
        </section>

        {/* ─── Features Section ─── */}
        <section className="section" id="features" aria-labelledby="features-heading">
          <div className="container">
            <Reveal>
              <div className="section-header">
                <span className="section-overline">Features</span>
                <h2 className="section-title" id="features-heading">Everything you need.<br />Nothing you don't.</h2>
              </div>
            </Reveal>
            <div className="feature-showcase">
              <FeatureCard icon="🧘" title="Guided Meditations" description="16 evidence-based sessions: mindfulness, body scan, loving-kindness, progressive muscle relaxation, NSDR, sound bath, sleep prep, and more — each with studio-quality synthesized voice guidance and nature video backgrounds." accent="#4A8C6F" index={0} />
              <FeatureCard icon="🌬️" title="Breathing Exercises" description="Box breathing, 4-7-8 sleep technique, coherent breathing, calming breath, and energizing patterns. An animated circle guides you through each phase with haptic feedback." accent="#7EB8C9" index={1} />
              <FeatureCard icon="🎵" title="Ambient Sounds" description="Rain, ocean, forest, fireplace, singing bowl, Om chanting, and ambient beds. Mix multiple sounds. Bundled with the app — works offline." accent="#7B68EE" index={2} />
            </div>
          </div>
        </section>

        {/* ─── Immersive / Experience Section ─── */}
        <section className="section section-dark" aria-labelledby="experience-heading">
          <div className="container">
            <Reveal>
              <div className="section-header">
                <span className="section-overline">Experience</span>
                <h2 className="section-title" id="experience-heading">Immersive by design</h2>
                <p className="section-desc">Every session is accompanied by nature videos, ambient audio, and gentle synthesized guidance to help you settle.</p>
              </div>
            </Reveal>
            <div className="experience-grid">
              {[
                { icon: '🎬', title: 'Video Backgrounds', desc: 'Ocean, forest, rain, river, fire, singing bowl, and Om videos play behind the experience.' },
                { icon: '🗣️', title: 'Voice Guidance', desc: 'Studio-quality synthesized narration (AI text-to-speech) reads each instruction. Toggle on or off.' },
                { icon: '📿', title: 'Multi-faith Prayers', desc: 'Twenty traditional texts, five each from the Buddhist, Christian, Islamic and Jewish traditions, rendered respectfully.' },
                { icon: '📊', title: 'Track Progress', desc: 'Your streak, minutes practised, a consistency heatmap of recent weeks, and badges. Stored locally on your device.' },
                { icon: '⏱️', title: 'Custom Timer', desc: 'Choose how long to sit and a background sound or nature video; a bell of your choice marks the start and the end.' },
                { icon: '✨', title: 'Glassmorphism UI', desc: 'Frosted glass cards float over video backgrounds. Minimal, modern, designed to calm.' },
              ].map((item, i) => (
                <Reveal key={item.title} delay={i * 0.08}>
                  <MagneticCard className="exp-card" strength={6}>
                    <div className="exp-icon" aria-hidden="true">{item.icon}</div>
                    <h3>{item.title}</h3>
                    <p>{item.desc}</p>
                  </MagneticCard>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ─── Privacy Section (honest, replaces old "no data ever") ─── */}
        <section className="section section-philosophy" id="privacy" aria-labelledby="privacy-heading">
          <div className="container container-narrow">
            <div className="philosophy-content">
              <Reveal>
                <span className="section-overline">Privacy</span>
                <h2 className="philosophy-title" id="privacy-heading">Private by default</h2>
                <p className="section-desc privacy-lead">
                  Local-first by design. Your practice, your health readings and
                  your conversations stay on your phone. There is no analytics or
                  crash-report server, and the AI companion never sends your chats
                  anywhere.
                </p>
              </Reveal>
              <div className="philosophy-grid">
                {[
                  { title: 'Local-first by default', desc: 'Sessions, streaks, moods, settings, and chat history live on your device. Chat history is encrypted at rest.' },
                  { title: 'On-device AI companion', desc: 'The Nightglass Plus companion runs Qwen3-0.6B on your phone — your messages never leave it.' },
                  { title: 'No cloud AI', desc: 'No chat server, no API key, no account, no device ID. Nothing you type to the companion is sent anywhere.' },
                  { title: 'Nothing sent for analytics', desc: 'A tally of which features are used and a log of app errors stay on your phone. There is no analytics or crash-report server and no switch to share them; Clear all data erases both.' },
                  { title: 'No sign-in, ever', desc: 'Your account is a private id on your phone. No email, no password.' },
                  { title: 'Works offline', desc: 'Meditations, breathing, sounds, the timer — and the companion, after its one-time download — all work without a connection.' },
                ].map((item, i) => (
                  <Reveal key={item.title} delay={i * 0.08} direction={i % 2 === 0 ? 'left' : 'right'}>
                    <motion.div
                      className="phil-item"
                      whileHover={{ x: 6 }}
                      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                    >
                      <div className="phil-check" aria-hidden="true">✓</div>
                      <div>
                        <h4>{item.title}</h4>
                        <p>{item.desc}</p>
                      </div>
                    </motion.div>
                  </Reveal>
                ))}
              </div>
              <Reveal delay={0.1}>
                <p className="privacy-link-row">
                  Read the full, specific breakdown in our{' '}
                  <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
                </p>
              </Reveal>
            </div>
          </div>
        </section>

        {/* ─── AI Companion & Safety Section ─── */}
        <section className="section" id="ai" aria-labelledby="ai-heading">
          <div className="container container-narrow">
            <Reveal>
              <div className="section-header">
                <span className="section-overline">AI companion &amp; safety</span>
                <h2 className="section-title" id="ai-heading">A wellbeing chat that respects you</h2>
                <p className="section-desc">
                  An optional AI companion to reflect with — running on your phone,
                  with honest limits and crisis-support resources built in.
                </p>
              </div>
            </Reveal>

            <div className="ai-grid">
              {[
                { icon: '📱', title: 'Runs on your phone', desc: 'Luma runs Qwen3-0.6B, an open model, entirely on your phone. Messages are processed locally and never leave it — no server, no API key, no account.' },
                { icon: '⬇️', title: 'Part of Nightglass Plus', desc: 'Included with Nightglass Plus. Needs a supported Android phone (64-bit, 4 GB of memory or more) and a one-time download of about 344 MB through Google Play. After that it works offline.' },
                { icon: '🆘', title: 'Crisis support built in', desc: 'A message that shows signs of crisis never reaches Luma: the app answers it itself, names a helpline for your country where the app has one, and opens Crisis Support, which also lists a directory of helplines worldwide.' },
                { icon: '🌐', title: 'Honest language caveat', desc: 'Crisis detection is currently strongest in English. If it does not recognize your wording, the app still shows you how to reach help — it degrades gracefully.' },
              ].map((item, i) => (
                <Reveal key={item.title} delay={i * 0.08}>
                  <MagneticCard className="exp-card ai-card" strength={6}>
                    <div className="exp-icon" aria-hidden="true">{item.icon}</div>
                    <h3>{item.title}</h3>
                    <p>{item.desc}</p>
                  </MagneticCard>
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.1}>
              <div className="ai-disclaimer" role="note">
                <strong>Not a substitute for professional care.</strong> The AI chat
                is a self-help aid, not medical, psychological, or emergency care.
                Replies are AI-generated and may be inaccurate. If you are in crisis
                or danger, contact your local emergency services or a crisis line
                immediately.
              </div>
            </Reveal>
          </div>
        </section>

        {/* ─── Science Section ─── */}
        <section className="section section-dark" aria-labelledby="science-heading">
          <div className="container container-narrow">
            <Reveal>
              <div className="section-header">
                <span className="section-overline">Research</span>
                <h2 className="section-title" id="science-heading">Grounded in science</h2>
                <p className="section-desc">
                  These references support the techniques in the app. Nightglass
                  does not make medical claims and is not a medical device.
                </p>
              </div>
            </Reveal>
            <div className="science-list">
              {[
                { text: 'Mindfulness meditation is associated with reduced cortisol levels', source: 'Turakitwanakan et al., 2013' },
                { text: '8 weeks of practice increases grey-matter density in attention-linked brain regions', source: 'Hölzel et al., 2011 — Harvard Medical School' },
                { text: 'Mindfulness improves sleep quality and reduces insomnia severity', source: 'Black et al., 2015 — JAMA Internal Medicine' },
                { text: 'MBSR was found non-inferior to escitalopram for anxiety disorders', source: 'Hoge et al., 2023 — JAMA Psychiatry' },
                { text: 'Diaphragmatic breathing activates the parasympathetic nervous system', source: 'Ma et al., 2017' },
                { text: 'Loving-kindness meditation increases positive emotions over time', source: 'Fredrickson et al., 2008' },
              ].map((item, i) => (
                <ScienceItem key={item.source} text={item.text} source={item.source} index={i} />
              ))}
            </div>
          </div>
        </section>

        {/* ─── Pricing / Tiers Section ─── */}
        <section className="section" id="pricing" aria-labelledby="pricing-heading">
          <div className="container">
            <Reveal>
              <div className="section-header">
                <span className="section-overline">Pricing</span>
                <h2 className="section-title" id="pricing-heading">Free core. Optional Plus.</h2>
                <p className="section-desc">
                  The full meditation experience is free. Nightglass Plus adds the
                  on-device AI companion and the prayers — never required.
                </p>
              </div>
            </Reveal>

            <div className="pricing-grid">
              <Reveal delay={0.05} direction="left">
                <MagneticCard className="pricing-card" strength={5}>
                  <div className="pricing-head">
                    <h3 className="pricing-name">Free</h3>
                    <p className="pricing-price"><span className="pricing-amount">$0</span><span className="pricing-period">forever</span></p>
                    <p className="pricing-tagline">Everything you need to practice.</p>
                  </div>
                  <ul className="pricing-list">
                    <li>All 16 guided meditations</li>
                    <li>All breathing patterns &amp; the custom timer</li>
                    <li>All ambient sounds &amp; video backgrounds</li>
                    <li>Local progress tracking &amp; streaks</li>
                  </ul>
                  <a
                    href="#get"
                    className="btn-secondary pricing-cta"
                    onClick={scrollToId('get')}
                  >
                    Get the app
                  </a>
                </MagneticCard>
              </Reveal>

              <Reveal delay={0.12} direction="right">
                <MagneticCard className="pricing-card pricing-card--premium" strength={5}>
                  <div className="pricing-flag">Coming soon</div>
                  <div className="pricing-head">
                    <h3 className="pricing-name">Nightglass Plus</h3>
                    <p className="pricing-price">
                      <span className="pricing-amount">$39</span>
                      <span className="pricing-period">one-time*</span>
                    </p>
                    <p className="pricing-tagline">An AI companion that runs on your phone. No subscription.</p>
                  </div>
                  <ul className="pricing-list">
                    <li>Everything in Free</li>
                    <li>Talk to Luma — AI companion running Qwen3-0.6B on your phone</li>
                    <li>Supported Android phones; one-time download through Google Play</li>
                    <li>All 20 multi-faith prayers &amp; mantras</li>
                  </ul>
                  <Waitlist />
                  <p className="pricing-note">
                    *One-time purchase, no subscription. Billing is not live yet; the
                    waitlist is informational only and you will not be charged.
                  </p>
                </MagneticCard>
              </Reveal>
            </div>
          </div>
        </section>

        {/* ─── Get the app / CTA Section ─── */}
        <section className="section-cta" id="get" aria-labelledby="get-heading">
          <div className="container">
            <Reveal>
              <MagneticCard className="cta-content" strength={4}>
                <h2 className="cta-title" id="get-heading">Start your journey</h2>
                <p className="cta-desc">
                  Free to start, private by default. Your first meditation is
                  60 seconds away.
                </p>
                <div className="cta-actions">
                  {PLAY_STORE_URL ? (
                    <motion.a
                      href={PLAY_STORE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-primary btn-large"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      aria-label="Get Nightglass on Google Play"
                    >
                      <span className="btn-icon" aria-hidden="true">▶</span>
                      Get it on Google Play
                    </motion.a>
                  ) : (
                    <span className="store-badge store-badge--soon" aria-label="Google Play — coming soon">
                      <span className="btn-icon" aria-hidden="true">▶</span>
                      Google Play &mdash; coming soon
                    </span>
                  )}

                  {APP_STORE_URL ? (
                    <motion.a
                      href={APP_STORE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-secondary btn-large"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.97 }}
                      aria-label="Download Nightglass on the App Store"
                    >
                      <span className="btn-icon" aria-hidden="true"></span>
                      Download on the App Store
                    </motion.a>
                  ) : (
                    <span className="store-badge store-badge--soon" aria-label="App Store — coming soon">
                      <span className="btn-icon" aria-hidden="true"></span>
                      App Store &mdash; coming soon
                    </span>
                  )}
                </div>

                <p className="cta-meta">
                  Direct APK for sideloading on{' '}
                  <a href={RELEASES_URL} target="_blank" rel="noopener noreferrer">GitHub Releases</a>
                  {' '}&middot; Android &middot; No account required
                </p>
              </MagneticCard>
            </Reveal>
          </div>
        </section>
      </main>

      {/* ─── Footer ─── */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <span className="nav-logo" aria-hidden="true">◉</span>
              <span>Nightglass</span>
            </div>
            <p className="footer-text">Private by default. Built for your peace of mind.</p>
            <nav className="footer-links" aria-label="Footer">
              <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">Privacy Policy</a>
              <a href={GITHUB_URL} target="_blank" rel="noopener noreferrer">GitHub</a>
              <a href={RELEASES_URL} target="_blank" rel="noopener noreferrer">Releases</a>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
